/**
 * ACCESS CONTROL TYPES
 *
 * Defines the access control system for the application, including:
 * - User rights (binary permissions assigned to users)
 * - Access groups (document-level access control via group membership)
 *
 * ## User Rights
 * User rights are binary permissions stored on the User document (`users/{userId}.userRights`).
 * They control what actions a user can perform system-wide. Rights are checked in:
 * - Firestore security rules via `hasUserRight()` function
 * - Backend functions for authorization
 * - Frontend UI for conditional rendering
 *
 * Rights follow a hierarchical pattern:
 * - `*Admin`: Full administrative access to configuration (not data)
 * - `*Read`: Read-only access to data
 * - `*Write`: Read and write access to data (implies read)
 * - `*Archive`: Permission to archive/restore
 * - `*Delete`: Permission to permanently delete (usually requires archive first)
 *
 * ## Access Groups
 * Access groups provide document-level access control. Each document can specify
 * which groups have read, write, or admin access. Users gain access through
 * security group membership (`users/{userId}.securityGroups`).
 *
 * Unlike user rights, access group permissions are exclusionary:
 * - `readGroupIds`: Grants read-only access
 * - `writeGroupIds`: Grants write-only access (must also be in readGroupIds for read)
 * - `adminGroupIds`: Can modify the accessGroups field itself
 *
 * @module AccessControl
 */

/**
 * Valid user right identifiers for permissions.
 * These correspond to the rights checked in firestore.rules via hasUserRight().
 * Also used in backend functions.
 */
export type UserRightId =
    | 'orgsAdmin'
    | 'formsAdmin'
    | 'formsRead'
    | 'formsWrite'
    | 'formsDelete'
    | 'formsArchive'
    | 'recordsRead'
    | 'recordsWrite'
    | 'recordsAdmin'
    | 'recordsDelete'
    | 'tablesAdmin'
    | 'workflowsAdmin'
    | 'metricsRead'
    | 'metricsOrgRead'
    | 'metricsAdmin'
    | 'attachmentsWrite'
    | 'attachmentsDelete'
    | 'aiOperator'
    | 'aiAdmin'
    | 'aiAgent'
    | 'aiMcp'
    | 'fileSync'
    | 'pricebookRead'
    | 'pricebookPublish'
    | 'ledgerRead'
    | 'ledgerPost'
    | 'ledgerCommit'
    | 'ledgerAdmin';

/**
 * User rights map.
 * Keys are right IDs, values are always `true` when the user has the right.
 */
export type UserRights = Partial<Record<UserRightId, true>>;

/**
 * Access control groups for documents.
 * Groups are exclusionary: readGroupIds grants read only, writeGroupIds grants write only.
 * Users typically need membership in multiple groups for full access.
 * Note: For userRights, write implies read. For accessGroups, access levels are independent.
 */
export type AccessGroups = {
    /** Groups with read access */
    readGroupIds?: string[];
    /** Groups with write access */
    writeGroupIds?: string[];
    /** Groups with admin access (can modify accessGroups, does not grant read/write) */
    adminGroupIds?: string[];
};

/**
 * Metadata for a user right, including display labels and descriptions.
 */
export type UserRightMetadata = {
    id: UserRightId;
    shortLabel: string;
    longDescription: string;
};

/**
 * Complete metadata for all user rights in the system.
 * Use this for rendering permission UIs, tooltips, and documentation.
 */
export const USER_RIGHTS_METADATA: Record<UserRightId, UserRightMetadata> = {
    orgsAdmin: {
        id: 'orgsAdmin',
        shortLabel: 'Org Admin',
        longDescription: 'Full administrative access to organization settings and user management. Does not grant access to organization data directly. Org Admin users cannot remove themselves from the organization.',
    },
    formsAdmin: {
        /** Forms admin access allows users to manage form configuration, does not grant access to forms nor form versions. */
        id: 'formsAdmin',
        shortLabel: 'Forms Admin',
        longDescription: 'Can manage form types and UI layouts. Does not grant access to forms content.',
    },
    formsRead: {
        /** Forms read access allows users to view forms and form versions. */
        id: 'formsRead',
        shortLabel: 'Forms Read',
        longDescription: 'Read-only access to view forms content.',
    },
    formsWrite: {
        /** Forms read and write access allows users to modify forms and form versions. Form creation is controlled by backend workflows. */
        id: 'formsWrite',
        shortLabel: 'Forms Write',
        longDescription: 'Read and write access to forms content.',
    },
    formsDelete: {
        /** Form deletion requires archival first. This right does not grant direct form deletion rights to the `forms/` collection. */
        id: 'formsDelete',
        shortLabel: 'Forms Delete',
        longDescription: 'Permission to delete forms permanently.',
    },
    formsArchive: {
        id: 'formsArchive',
        shortLabel: 'Forms Archive',
        longDescription: 'Permission to archive and restore forms.',
    },
    /** Entities and entity records are labeled Records in the UI. */
    recordsAdmin: {
        id: 'recordsAdmin',
        shortLabel: 'Records Admin',
        longDescription: 'Can manage records layouts, rules and workflows. Does not grant access to records content.',
    },
    recordsRead: {
        /** Records read access allows users to read entities and entity records. */
        id: 'recordsRead',
        shortLabel: 'Records Read',
        longDescription: 'Read-only access to view records.',
    },
    recordsWrite: {
        /** Records read and write access allows users to create and modify entity records but not entities. */
        id: 'recordsWrite',
        shortLabel: 'Records Write',
        longDescription: 'Create, read and write access to records.',
    },
    recordsDelete: {
        /** Record deletion requires archival first. This right does not grant direct record deletion rights to the `entities/{entityId}/records/` collection. */
        id: 'recordsDelete',
        shortLabel: 'Records Delete',
        longDescription: 'Permission to permanently delete records.',
    },
    tablesAdmin: {
        id: 'tablesAdmin',
        shortLabel: 'Tables Admin',
        longDescription: 'Full administrative access to table types and configurations.',
    },
    workflowsAdmin: {
        id: 'workflowsAdmin',
        shortLabel: 'Workflows Admin',
        longDescription: 'Full administrative access to workflow definitions.',
    },
    metricsRead: {
        id: 'metricsRead',
        shortLabel: 'Metrics Read',
        longDescription: 'Read-only access to view analytics and metrics.',
    },
    metricsOrgRead: {
        id: 'metricsOrgRead',
        shortLabel: 'Metrics Org Read',
        longDescription: 'Read-only access to view analytics at every level, Org included. Grants no write access.',
    },
    metricsAdmin: {
        id: 'metricsAdmin',
        shortLabel: 'Metrics Admin',
        longDescription: 'Full administrative access to metrics configuration.',
    },
    /** There are no `attachmentsRead` right. Reads are granted by the document with the attachment. */
    attachmentsWrite: {
        /** Attachments read and write access allows users to upload and modify file attachments. */
        id: 'attachmentsWrite',
        shortLabel: 'Attachments Write',
        longDescription: 'Read and write access to file attachments.',
    },
    attachmentsDelete: {
        id: 'attachmentsDelete',
        shortLabel: 'Attachments Delete',
        longDescription: 'Permission to delete file attachments.',
    },
    aiOperator: {
        id: 'aiOperator',
        shortLabel: 'AI Operator',
        longDescription: 'Operational access to AI features including activity logs, email analyses, and extraction results. Does not grant access to AI configuration.',
    },
    aiAdmin: {
        id: 'aiAdmin',
        shortLabel: 'AI Admin',
        longDescription: 'Full administrative access to AI configuration. Includes all AI Operator access plus the ability to manage AI settings.',
    },
    aiAgent: {
        id: 'aiAgent',
        shortLabel: 'AI Agent',
        longDescription: 'Access to the in-app AI Agent panel for interactive chat and agentic task execution.',
    },
    aiMcp: {
        id: 'aiMcp',
        shortLabel: 'AI MCP',
        longDescription: 'Allows the user\'s AI agent to see and call tools from registered external MCP servers. Server registration itself requires Organization Admin.',
    },
    /** Gates the Syncretic File Sync surfaces only. It grants no data access of its own: a pin lives on the holder's own user document, and what the desktop agent may read or write is decided by the rights above. */
    fileSync: {
        id: 'fileSync',
        shortLabel: 'File Sync (Beta)',
        longDescription: 'Access to the Syncretic File Sync surfaces: the "Files on my computer" settings pane, and the "Keep on my computer" controls that copy a document or one of its files to your own computer and keep it up to date. Grants no data access of its own.',
    },
    /** Pricebook rights gate the pricebook registry. Order intake reads it through `aiOperator` instead, and may fix pages and request sign-off, but not publish. */
    pricebookRead: {
        id: 'pricebookRead',
        shortLabel: 'Pricebook Read',
        longDescription: 'Read access to the pricebook registry, its intake jobs and its editions, and the ability to start a pricebook upload.',
    },
    /** Does not imply `pricebookRead`; a holder who should see the registry needs both. */
    pricebookPublish: {
        id: 'pricebookPublish',
        shortLabel: 'Pricebook Publish',
        longDescription: 'Publish a pricebook edition, which supersedes the previous edition and re-checks open schedules against the new one. Does not include Pricebook Read.',
    },
    /** Ledger rights gate the reconciliation ledger in Postgres, not Firestore; `orgsAdmin` is deliberately not a superset of them. */
    ledgerRead: {
        id: 'ledgerRead',
        shortLabel: 'Ledger Read',
        longDescription: 'Read-only access to ledger balances, entries and views.',
    },
    ledgerPost: {
        id: 'ledgerPost',
        shortLabel: 'Ledger Post',
        longDescription: 'Post receipts, imports and manual entries to a ledger. Includes Ledger Read.',
    },
    ledgerCommit: {
        id: 'ledgerCommit',
        shortLabel: 'Ledger Commit',
        longDescription: 'Accept match proposals, which posts allocations, and close periods. Includes Ledger Read.',
    },
    ledgerAdmin: {
        id: 'ledgerAdmin',
        shortLabel: 'Ledger Admin',
        longDescription: 'Manage ledger definitions. Does not grant access to ledger data.',
    },
};
